// src/data.tsx
import {
  useCallback as useCallback2,
  useEffect as useEffect2,
  useEffectEvent as useEffectEvent2,
  useId as useId3,
  useLayoutEffect as useLayoutEffect2,
  useRef as useRef3,
  useState as useState4
} from "react";

// src/display.tsx
import {
  Fragment,
  useState
} from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var TONE_GLYPHS = {
  info: { name: "info", paths: ["M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18z", "M12 11v5", "M12 8h.01"] },
  success: {
    name: "check",
    paths: ["M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18z", "M8 12.5l2.5 2.5l5-5.5"]
  },
  warning: {
    name: "warning",
    paths: [
      "M10.3 4.2L2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0z",
      "M12 9.5v4",
      "M12 17h.01"
    ]
  },
  danger: {
    name: "alert",
    paths: ["M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18z", "M12 7.5v5.5", "M12 16.5h.01"]
  }
};
function ToneIcon({ tone }) {
  const { name, paths } = TONE_GLYPHS[tone];
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      "data-icon": name,
      fill: "none",
      height: "1em",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "2",
      viewBox: "0 0 24 24",
      width: "1em",
      children: paths.map((path) => /* @__PURE__ */ jsx("path", { d: path }, path))
    }
  );
}
function Alert({ tone = "info", title, children, action, role }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `tny-alert tny-alert-${tone}`,
      role: role ?? (tone === "danger" || tone === "warning" ? "alert" : "status"),
      children: [
        /* @__PURE__ */ jsx("span", { className: "tny-alert-icon", children: /* @__PURE__ */ jsx(ToneIcon, { tone }) }),
        /* @__PURE__ */ jsxs("div", { className: "tny-alert-content", children: [
          title ? /* @__PURE__ */ jsx("p", { className: "tny-alert-title", children: title }) : null,
          /* @__PURE__ */ jsx("div", { className: "tny-alert-body", children }),
          action ? /* @__PURE__ */ jsx("div", { className: "tny-alert-action", children: action }) : null
        ] })
      ]
    }
  );
}
function Card({ title, actions, headingLevel = 2, children }) {
  const Heading = headingLevel === 3 ? "h3" : "h2";
  return /* @__PURE__ */ jsxs("section", { className: "tny-card", children: [
    title || actions ? /* @__PURE__ */ jsxs("div", { className: "tny-card-header", children: [
      title ? /* @__PURE__ */ jsx(Heading, { className: "tny-card-title", children: title }) : null,
      actions ? /* @__PURE__ */ jsx("div", { className: "tny-card-actions", children: actions }) : null
    ] }) : null,
    /* @__PURE__ */ jsx("div", { className: "tny-card-body", children })
  ] });
}
function DescriptionList({ items, stacked = false }) {
  return /* @__PURE__ */ jsx("dl", { className: stacked ? "tny-dl tny-dl-stacked" : "tny-dl", children: items.map(({ term, value }, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: a term can be markup, and the facts keep their order.
    /* @__PURE__ */ jsxs("div", { className: "tny-dl-item", children: [
      /* @__PURE__ */ jsx("dt", { className: "tny-dl-term", children: term }),
      /* @__PURE__ */ jsx("dd", { className: "tny-dl-value", children: value })
    ] }, index)
  )) });
}
function PageHeader({
  title,
  description,
  back,
  media,
  actions,
  headingRef
}) {
  const text = /* @__PURE__ */ jsxs("div", { className: "tny-page-header-text", children: [
    /* @__PURE__ */ jsx("h1", { className: "tny-page-header-title", ref: headingRef, tabIndex: headingRef ? -1 : void 0, children: title }),
    description ? /* @__PURE__ */ jsx("p", { className: "tny-page-header-description", children: description }) : null
  ] });
  return /* @__PURE__ */ jsxs("div", { className: "tny-page-header", children: [
    back ? /* @__PURE__ */ jsx("div", { className: "tny-page-header-back", children: back }) : null,
    /* @__PURE__ */ jsxs("div", { className: "tny-page-header-row", children: [
      media ? /* @__PURE__ */ jsxs("div", { className: "tny-page-header-lead", children: [
        /* @__PURE__ */ jsx("div", { className: "tny-page-header-media", children: media }),
        text
      ] }) : text,
      actions ? /* @__PURE__ */ jsx("div", { className: "tny-page-header-actions", children: actions }) : null
    ] })
  ] });
}
function Badge({ tone = "neutral", children }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: `tny-badge tny-badge-${tone}`,
      title: typeof children === "string" ? children : void 0,
      children
    }
  );
}
function Muted({ children }) {
  return /* @__PURE__ */ jsx("span", { className: "tny-muted", children });
}
function breakable(text) {
  return text.split(/(?<=[@./?&=_])/).map((part, index, parts) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one string never reorder.
    /* @__PURE__ */ jsxs(Fragment, { children: [
      part,
      index < parts.length - 1 ? /* @__PURE__ */ jsx("wbr", {}) : null
    ] }, index)
  ));
}
function initials(name) {
  const letters = name.split(/\s+/).flatMap((word) => word.match(/[\p{L}\p{N}]/u) ?? []);
  const first = letters[0] ?? "";
  const last = letters.length > 1 ? letters[letters.length - 1] ?? "" : "";
  return `${first}${last}`.toLocaleUpperCase();
}
function Avatar({ name, src, size = "md" }) {
  const [failed, setFailed] = useState(null);
  const image = src && src !== failed ? src : null;
  return /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: `tny-avatar tny-avatar-${size}`, children: image ? (
    // No referrer: the picture's host never learns which page showed it.
    /* @__PURE__ */ jsx(
      "img",
      {
        alt: "",
        className: "tny-avatar-image",
        onError: () => setFailed(image),
        referrerPolicy: "no-referrer",
        src: image
      }
    )
  ) : initials(name) });
}
function StatTile({ label, value, hint, format }) {
  return /* @__PURE__ */ jsxs("dl", { className: "tny-stat", children: [
    /* @__PURE__ */ jsx("dt", { className: "tny-stat-label", children: label }),
    /* @__PURE__ */ jsx("dd", { className: "tny-stat-value", children: typeof value === "number" ? new Intl.NumberFormat(void 0, format).format(value) : value }),
    hint ? /* @__PURE__ */ jsx("dd", { className: "tny-stat-hint", children: hint }) : null
  ] });
}
var TIME_FORMAT = { dateStyle: "medium", timeStyle: "short" };
function Timeline({ label, items, emptyLabel, locale }) {
  if (items.length === 0) return /* @__PURE__ */ jsx("p", { className: "tny-timeline-empty", children: emptyLabel });
  const format = new Intl.DateTimeFormat(locale, TIME_FORMAT);
  return /* @__PURE__ */ jsx(
    "ol",
    {
      "aria-label": label,
      className: "tny-timeline",
      role: "list",
      children: items.map((item) => {
        const date = new Date(item.at);
        return /* @__PURE__ */ jsxs("li", { className: `tny-timeline-item tny-timeline-${item.tone ?? "neutral"}`, children: [
          /* @__PURE__ */ jsx("div", { className: "tny-timeline-title", children: item.title }),
          /* @__PURE__ */ jsx("time", { className: "tny-timeline-time", dateTime: item.at, children: Number.isNaN(date.getTime()) ? item.at : format.format(date) }),
          item.description ? /* @__PURE__ */ jsx("div", { className: "tny-timeline-description", children: item.description }) : null
        ] }, item.id);
      })
    }
  );
}
function Skeleton({ lines = 1, width, height }) {
  return /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "tny-skeleton", children: Array.from({ length: lines }, (_, index) => /* @__PURE__ */ jsx(
    "span",
    {
      className: "tny-skeleton-line",
      style: { width, height }
    },
    index
  )) });
}
function EmptyState({
  title,
  description,
  action,
  icon,
  tone = "neutral"
}) {
  return /* @__PURE__ */ jsxs("div", { className: `tny-empty tny-empty-${tone}`, children: [
    icon ? /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className: "tny-empty-icon", children: icon }) : null,
    /* @__PURE__ */ jsx("h2", { className: "tny-empty-title", children: title }),
    description ? /* @__PURE__ */ jsx("p", { className: "tny-empty-description", children: description }) : null,
    action ? /* @__PURE__ */ jsx("div", { className: "tny-empty-action", children: action }) : null
  ] });
}

// src/form.tsx
import {
  cloneElement,
  useId,
  useRef,
  useState as useState2
} from "react";
import { Fragment as Fragment2, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var MAX_OPTIONS_WITHOUT_SEARCH = 8;
function cx(...names) {
  return names.filter(Boolean).join(" ");
}
function shownMessage({ hint, warning, error }) {
  if (error) return "error";
  if (warning) return "warning";
  return hint ? "hint" : void 0;
}
function describedBy(id, messages, own) {
  const shown = shownMessage(messages);
  return [own, shown && `${id}-${shown}`].filter(Boolean).join(" ") || void 0;
}
function Messages({
  id,
  liveHint = false,
  ...messages
}) {
  const shown = shownMessage(messages);
  return /* @__PURE__ */ jsxs2(Fragment2, { children: [
    liveHint || shown === "hint" ? /* @__PURE__ */ jsx2("p", { "aria-live": liveHint ? "polite" : void 0, className: "tny-hint", id: `${id}-hint`, children: shown === "hint" ? messages.hint : null }) : null,
    shown === "warning" ? /* @__PURE__ */ jsxs2("p", { className: "tny-warning", id: `${id}-warning`, role: "status", children: [
      /* @__PURE__ */ jsx2("span", { className: "tny-warning-icon", children: /* @__PURE__ */ jsx2(ToneIcon, { tone: "warning" }) }),
      messages.warning
    ] }) : null,
    shown === "error" ? /* @__PURE__ */ jsx2("p", { className: "tny-error", id: `${id}-error`, role: "alert", children: messages.error }) : null
  ] });
}
function RequiredMark() {
  return /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", className: "tny-required", children: "*" });
}
function Button({
  variant = "primary",
  size = "md",
  loading = false,
  type = "button",
  className,
  disabled,
  children,
  "aria-busy": busy,
  ...props
}) {
  return /* @__PURE__ */ jsx2(
    "button",
    {
      ...props,
      "aria-busy": loading || busy || void 0,
      className: cx(
        "tny-button",
        `tny-button-${variant}`,
        size === "sm" && "tny-button-sm",
        className
      ),
      disabled: disabled || loading,
      type,
      children: loading ? /* @__PURE__ */ jsxs2(Fragment2, { children: [
        /* @__PURE__ */ jsx2("span", { className: "tny-button-label", children }),
        /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", className: "tny-spinner" })
      ] }) : children
    }
  );
}
function IconButton({
  label,
  icon,
  variant = "ghost",
  title = label,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx2(
    Button,
    {
      ...props,
      "aria-label": label,
      className: cx("tny-icon-button", className),
      title,
      variant,
      children: /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", className: "tny-button-icon", children: icon })
    }
  );
}
function Input({ className, prefix, suffix, size, ...props }) {
  const short = size === "short" && "tny-input-short";
  if (prefix == null && suffix == null) {
    return /* @__PURE__ */ jsx2("input", { ...props, className: cx("tny-input", short, className) });
  }
  const adorned = { ...props, className, prefix, short, suffix };
  return props.id ? /* @__PURE__ */ jsx2(AdornedInput, { ...adorned, base: props.id }) : /* @__PURE__ */ jsx2(BareAdornedInput, { ...adorned });
}
function BareAdornedInput(props) {
  return /* @__PURE__ */ jsx2(AdornedInput, { ...props, base: useId() });
}
function AdornedInput({
  base,
  className,
  prefix,
  suffix,
  short,
  ...props
}) {
  const prefixId = spoken(prefix) ? `${base}-prefix` : void 0;
  const suffixId = spoken(suffix) ? `${base}-suffix` : void 0;
  const describedBy2 = [prefixId, suffixId, props["aria-describedby"]].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs2("span", { className: cx("tny-input-group", short), children: [
    prefix != null ? /* @__PURE__ */ jsx2("span", { className: "tny-input-adornment", id: prefixId, children: prefix }) : null,
    /* @__PURE__ */ jsx2(
      "input",
      {
        ...props,
        "aria-describedby": describedBy2 || void 0,
        className: cx("tny-input", className)
      }
    ),
    suffix != null ? /* @__PURE__ */ jsx2("span", { className: "tny-input-adornment", id: suffixId, children: suffix }) : null
  ] });
}
function spoken(adornment) {
  return typeof adornment === "string" || typeof adornment === "number";
}
function Select({ className, ...props }) {
  return /* @__PURE__ */ jsxs2("span", { className: "tny-select-control", children: [
    /* @__PURE__ */ jsx2("select", { ...props, className: cx("tny-select", className) }),
    /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", className: "tny-select-chevron" })
  ] });
}
function Textarea({ className, ...props }) {
  return /* @__PURE__ */ jsx2("textarea", { ...props, className: cx("tny-textarea", className) });
}
function Field({
  label,
  hint,
  warning,
  error,
  required = false,
  liveHint = false,
  aside,
  asideDescribes = false,
  children
}) {
  const id = useId();
  const control = children;
  const controlId = control.props.id ?? id;
  const wiring = {
    id: controlId,
    "aria-describedby": describedBy(
      id,
      { error, hint, warning },
      [control.props["aria-describedby"], asideDescribes && aside != null && `${id}-aside`].filter(Boolean).join(" ")
    )
  };
  if (error) wiring["aria-invalid"] = true;
  if (required) wiring.required = true;
  return /* @__PURE__ */ jsxs2("div", { className: "tny-field", children: [
    /* @__PURE__ */ jsxs2("div", { className: "tny-field-label", children: [
      /* @__PURE__ */ jsx2("label", { className: "tny-label", htmlFor: controlId, children: label }),
      required ? /* @__PURE__ */ jsx2(RequiredMark, {}) : null,
      aside != null ? /* @__PURE__ */ jsx2("span", { className: "tny-field-aside", id: `${id}-aside`, children: aside }) : null
    ] }),
    cloneElement(control, wiring),
    /* @__PURE__ */ jsx2(Messages, { error, hint, id, liveHint, warning })
  ] });
}
function Choice({
  type,
  card = false,
  label,
  hint,
  error,
  id,
  className,
  ...props
}) {
  const ownId = useId();
  const inputId = id ?? ownId;
  return /* @__PURE__ */ jsxs2("div", { className: cx("tny-choice", card && "tny-choice-card"), children: [
    /* @__PURE__ */ jsx2(
      "input",
      {
        ...props,
        "aria-describedby": describedBy(ownId, { error, hint }, props["aria-describedby"]),
        "aria-invalid": error ? true : props["aria-invalid"],
        className: cx("tny-choice-input", className),
        id: inputId,
        type
      }
    ),
    /* @__PURE__ */ jsxs2("div", { className: "tny-choice-text", children: [
      /* @__PURE__ */ jsx2("label", { className: "tny-choice-label", htmlFor: inputId, children: label }),
      props.required ? /* @__PURE__ */ jsx2(RequiredMark, {}) : null,
      /* @__PURE__ */ jsx2(Messages, { error, hint, id: ownId })
    ] })
  ] });
}
function Checkbox(props) {
  return /* @__PURE__ */ jsx2(Choice, { ...props, type: "checkbox" });
}
function Radio(props) {
  return /* @__PURE__ */ jsx2(Choice, { ...props, type: "radio" });
}
function FileInput({
  label,
  buttonLabel,
  accept,
  onFiles,
  fileName,
  emptyLabel,
  hint,
  error,
  disabled = false,
  busy = false
}) {
  const id = useId();
  return /* @__PURE__ */ jsxs2("div", { className: "tny-field tny-file", children: [
    /* @__PURE__ */ jsx2("div", { className: "tny-field-label", children: /* @__PURE__ */ jsx2("label", { className: "tny-label", htmlFor: `${id}-input`, children: label }) }),
    /* @__PURE__ */ jsxs2("div", { className: "tny-file-row", children: [
      /* @__PURE__ */ jsxs2("label", { className: "tny-button tny-button-secondary tny-file-button", children: [
        /* @__PURE__ */ jsx2(
          "input",
          {
            accept,
            "aria-busy": busy || void 0,
            "aria-describedby": describedBy(id, { error, hint }, `${id}-name`),
            "aria-invalid": error ? true : void 0,
            className: "tny-visually-hidden",
            disabled,
            id: `${id}-input`,
            onChange: (event) => {
              const files = [...event.currentTarget.files ?? []];
              event.currentTarget.value = "";
              if (files.length > 0) onFiles(files);
            },
            type: "file"
          }
        ),
        buttonLabel
      ] }),
      /* @__PURE__ */ jsx2("span", { className: "tny-file-name", id: `${id}-name`, title: fileName, children: fileName ?? emptyLabel })
    ] }),
    /* @__PURE__ */ jsx2(Messages, { error, hint, id })
  ] });
}
function namesOf(labels, count) {
  const shown = labels.slice(0, 2);
  const more = count - shown.length;
  return [shown.join(", "), more > 0 && `+${more}`].filter(Boolean).join(" ");
}
function MultiSelect({
  label,
  options,
  value,
  onChange,
  hint,
  error,
  required = false,
  disabled = false,
  name,
  summaryLabel,
  placeholder = "Choose\u2026",
  emptyLabel,
  searchLabel = "Search",
  requiredLabel = "Required"
}) {
  const id = useId();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const openAtPress = useRef(null);
  const [open, setOpen] = useState2(false);
  const [query, setQuery] = useState2("");
  const labels = options.filter((option) => value.includes(option.value)).map((option) => option.label);
  const summary = summaryLabel ? summaryLabel(value.length, labels) : value.length === 0 ? emptyLabel ?? placeholder : namesOf(labels, value.length);
  const search = query.trim().toLocaleLowerCase();
  const anchor = `--tny-anchor${id}`;
  const description = [describedBy(id, { error, hint }), required && `${id}-required`].filter(Boolean).join(" ");
  function rememberOpen() {
    openAtPress.current = open;
  }
  function closeOnEscape(event) {
    if (event.key !== "Escape" || !open) return;
    event.preventDefault();
    panelRef.current?.hidePopover();
    triggerRef.current?.focus();
  }
  function closeOnFocusOut(event) {
    const next = event.relatedTarget;
    if (!open || !(next instanceof Node)) return;
    if (triggerRef.current?.contains(next) || panelRef.current?.contains(next)) return;
    panelRef.current?.hidePopover();
  }
  function toggleOption(optionValue, checked) {
    const selected = new Set(value);
    if (checked) selected.add(optionValue);
    else selected.delete(optionValue);
    const ordered = options.map((option) => option.value);
    onChange([
      ...ordered.filter((candidate) => selected.has(candidate)),
      ...value.filter((candidate) => !ordered.includes(candidate))
    ]);
  }
  return /* @__PURE__ */ jsxs2("div", { className: "tny-field tny-multiselect", children: [
    /* @__PURE__ */ jsxs2("div", { className: "tny-field-label", children: [
      /* @__PURE__ */ jsx2(
        "label",
        {
          className: "tny-label",
          htmlFor: `${id}-trigger`,
          id: `${id}-label`,
          onPointerDown: rememberOpen,
          children: label
        }
      ),
      required ? /* @__PURE__ */ jsx2(RequiredMark, {}) : null
    ] }),
    /* @__PURE__ */ jsxs2(
      Button,
      {
        "aria-controls": `${id}-panel`,
        "aria-describedby": description || void 0,
        "aria-expanded": open,
        "aria-invalid": error ? true : void 0,
        "aria-label": `${label}: ${summary}`,
        className: "tny-multiselect-trigger",
        disabled,
        id: `${id}-trigger`,
        onBlur: closeOnFocusOut,
        onClick: () => {
          const wasOpen = openAtPress.current ?? open;
          openAtPress.current = null;
          panelRef.current?.togglePopover(!wasOpen);
        },
        onKeyDown: closeOnEscape,
        onPointerDown: rememberOpen,
        ref: triggerRef,
        style: { anchorName: anchor },
        variant: "secondary",
        children: [
          /* @__PURE__ */ jsx2(
            "span",
            {
              className: cx(
                "tny-multiselect-summary",
                value.length === 0 && emptyLabel === void 0 && "tny-multiselect-placeholder"
              ),
              children: summary
            }
          ),
          /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", className: "tny-multiselect-chevron" })
        ]
      }
    ),
    /* @__PURE__ */ jsxs2(
      "fieldset",
      {
        "aria-labelledby": `${id}-label`,
        className: "tny-multiselect-panel",
        disabled,
        id: `${id}-panel`,
        onBlur: closeOnFocusOut,
        onKeyDown: closeOnEscape,
        onToggle: (event) => {
          setOpen(event.newState === "open");
          if (event.newState === "closed") setQuery("");
        },
        popover: "auto",
        ref: panelRef,
        style: { positionAnchor: anchor },
        tabIndex: -1,
        children: [
          options.length > MAX_OPTIONS_WITHOUT_SEARCH ? /* @__PURE__ */ jsx2(Field, { label: searchLabel, children: /* @__PURE__ */ jsx2(
            Input,
            {
              autoComplete: "off",
              onChange: (event) => setQuery(event.currentTarget.value),
              onKeyDown: (event) => {
                if (event.key === "Enter") event.preventDefault();
              },
              type: "search",
              value: query
            }
          ) }) : null,
          /* @__PURE__ */ jsx2("div", { className: "tny-multiselect-options", children: options.map((option) => /* @__PURE__ */ jsx2(
            "div",
            {
              className: "tny-multiselect-option",
              hidden: !option.label.toLocaleLowerCase().includes(search),
              children: /* @__PURE__ */ jsx2(
                Checkbox,
                {
                  "aria-disabled": option.disabled && !value.includes(option.value) || void 0,
                  checked: value.includes(option.value),
                  hint: option.description,
                  label: option.label,
                  name,
                  onChange: (event) => {
                    if (event.currentTarget.getAttribute("aria-disabled") === "true") return;
                    toggleOption(option.value, event.currentTarget.checked);
                  },
                  value: option.value
                }
              )
            },
            option.value
          )) })
        ]
      }
    ),
    /* @__PURE__ */ jsx2(Messages, { error, hint, id }),
    required ? /* @__PURE__ */ jsx2("span", { className: "tny-visually-hidden", id: `${id}-required`, children: requiredLabel }) : null
  ] });
}

// src/overlay.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useId as useId2,
  useLayoutEffect,
  useMemo,
  useRef as useRef2,
  useState as useState3
} from "react";
import { Fragment as Fragment3, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
import { createElement } from "react";
var FOCUSABLE = [
  "a[href]",
  "area[href]",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "iframe",
  "audio[controls]",
  "video[controls]",
  '[contenteditable]:not([contenteditable="false"])',
  "[tabindex]"
].join(", ");
function CrossIcon() {
  return /* @__PURE__ */ jsx3(
    "svg",
    {
      "aria-hidden": "true",
      fill: "none",
      height: "1em",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeWidth: "2",
      viewBox: "0 0 24 24",
      width: "1em",
      children: /* @__PURE__ */ jsx3("path", { d: "M6 6l12 12M18 6L6 18" })
    }
  );
}
function isNamedRadio(element) {
  return element instanceof HTMLInputElement && element.type === "radio" && element.name !== "";
}
function sameGroup(a, b) {
  return a.name === b.name && a.form === b.form;
}
function isRendered(element) {
  if (typeof element.checkVisibility === "function") {
    return element.checkVisibility({ visibilityProperty: true });
  }
  for (let node = element; node; node = node.parentElement) {
    if (getComputedStyle(node).display === "none") return false;
  }
  return true;
}
function tabStops(root) {
  const focusable = [...root.querySelectorAll(FOCUSABLE)].filter(
    (element) => element.tabIndex >= 0 && !element.matches(":disabled") && !element.closest("[inert]") && isRendered(element)
  );
  return focusable.filter(
    (element) => !isNamedRadio(element) || element.checked || !focusable.some((other) => isNamedRadio(other) && other.checked && sameGroup(other, element))
  );
}
function isSameStop(element, stop) {
  return element === stop || isNamedRadio(element) && isNamedRadio(stop) && sameGroup(element, stop);
}
function focusInitial(dialog, closeButton) {
  const titleId = dialog.getAttribute("aria-labelledby");
  const initial = dialog.querySelector("[autofocus], [data-autofocus]") ?? tabStops(dialog).find((stop) => stop !== closeButton) ?? (titleId ? document.getElementById(titleId) : null);
  initial?.focus();
}
function useModal({
  open,
  onClose,
  locked = false,
  closeRef,
  returnFocusRef
}) {
  const ref = useRef2(null);
  const closedByBrowser = useEffectEvent((dialog) => {
    if (!locked) {
      onClose();
      return;
    }
    dialog.showModal();
    focusInitial(dialog, closeRef?.current);
  });
  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!open || !dialog) return;
    const opener = document.activeElement;
    if (!dialog.open) dialog.showModal();
    focusInitial(dialog, closeRef?.current);
    const onNativeClose = () => {
      if (!dialog.open) closedByBrowser(dialog);
    };
    dialog.addEventListener("close", onNativeClose);
    return () => {
      dialog.removeEventListener("close", onNativeClose);
      if (dialog.open) dialog.close();
      const target = returnFocusRef?.current ?? opener;
      const active = document.activeElement;
      const focusLost = !active || active === document.body || dialog.contains(active);
      if (target instanceof HTMLElement && target.isConnected && focusLost) target.focus();
    };
  }, [open, closeRef, returnFocusRef]);
  function onCancel(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!locked) onClose();
  }
  function onKeyDown(event) {
    if (event.key !== "Tab" || event.defaultPrevented) return;
    const dialog = event.currentTarget;
    const stops = tabStops(dialog);
    const first = stops[0];
    const last = stops.at(-1);
    if (!first || !last) return;
    const active = document.activeElement;
    const beforeFirst = active instanceof Node && Boolean(active.compareDocumentPosition(first) & Node.DOCUMENT_POSITION_FOLLOWING);
    const atEdge = event.shiftKey ? beforeFirst || isSameStop(active, first) : isSameStop(active, last);
    if (!atEdge) return;
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }
  return { ref, onCancel, onKeyDown };
}
function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  closeLabel = "Close",
  pending = false
}) {
  const id = useId2();
  const closeRef = useRef2(null);
  const modal = useModal({ open, onClose, closeRef, locked: pending });
  return /* @__PURE__ */ jsx3(
    "dialog",
    {
      "aria-describedby": description ? `${id}-description` : void 0,
      "aria-labelledby": `${id}-title`,
      className: `tny-dialog tny-dialog-${size}`,
      onCancel: modal.onCancel,
      onKeyDown: modal.onKeyDown,
      ref: modal.ref,
      children: open ? /* @__PURE__ */ jsxs3("div", { className: "tny-dialog-frame", children: [
        /* @__PURE__ */ jsxs3("div", { className: "tny-dialog-header", children: [
          /* @__PURE__ */ jsx3("h2", { className: "tny-dialog-title", id: `${id}-title`, tabIndex: -1, children: title }),
          /* @__PURE__ */ jsx3(
            IconButton,
            {
              "aria-disabled": pending || void 0,
              className: "tny-dialog-close",
              icon: /* @__PURE__ */ jsx3(CrossIcon, {}),
              label: closeLabel,
              onClick: pending ? void 0 : onClose,
              ref: closeRef
            }
          )
        ] }),
        description ? /* @__PURE__ */ jsx3("p", { className: "tny-dialog-description", id: `${id}-description`, children: description }) : null,
        /* @__PURE__ */ jsx3("div", { className: "tny-dialog-body", children }),
        footer ? /* @__PURE__ */ jsx3("div", { className: "tny-dialog-footer", children: footer }) : null
      ] }) : null
    }
  );
}
function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = "Cancel",
  closeLabel = "Close",
  danger = false,
  pending = false,
  onConfirm,
  onCancel
}) {
  const id = useId2();
  const closeRef = useRef2(null);
  const modal = useModal({ open, onClose: onCancel, closeRef, locked: pending });
  return /* @__PURE__ */ jsx3(
    "dialog",
    {
      "aria-describedby": `${id}-body`,
      "aria-labelledby": `${id}-title`,
      className: "tny-dialog tny-dialog-sm",
      onCancel: modal.onCancel,
      onKeyDown: modal.onKeyDown,
      ref: modal.ref,
      role: "alertdialog",
      children: open ? /* @__PURE__ */ jsxs3("div", { className: "tny-dialog-frame", children: [
        /* @__PURE__ */ jsxs3("div", { className: "tny-dialog-header", children: [
          /* @__PURE__ */ jsx3("h2", { className: "tny-dialog-title", id: `${id}-title`, children: title }),
          /* @__PURE__ */ jsx3(
            IconButton,
            {
              "aria-disabled": pending || void 0,
              className: "tny-dialog-close",
              icon: /* @__PURE__ */ jsx3(CrossIcon, {}),
              label: closeLabel,
              onClick: pending ? void 0 : onCancel,
              ref: closeRef
            }
          )
        ] }),
        /* @__PURE__ */ jsx3("div", { className: "tny-dialog-body", id: `${id}-body`, children: body }),
        /* @__PURE__ */ jsxs3("div", { className: "tny-dialog-footer", children: [
          /* @__PURE__ */ jsx3(
            Button,
            {
              "aria-disabled": pending || void 0,
              onClick: pending ? void 0 : onCancel,
              variant: "secondary",
              children: cancelLabel
            }
          ),
          /* @__PURE__ */ jsxs3(
            Button,
            {
              "aria-busy": pending || void 0,
              "aria-disabled": pending || void 0,
              onClick: pending ? void 0 : onConfirm,
              variant: danger ? "danger" : "primary",
              children: [
                pending ? /* @__PURE__ */ jsx3("span", { "aria-hidden": "true", className: "tny-spinner" }) : null,
                confirmLabel
              ]
            }
          )
        ] })
      ] }) : null
    }
  );
}
function rovingIndex(key, at, count, [previous, next]) {
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  if (key === next) return (at + 1) % count;
  if (key === previous) return (at - 1 + count) % count;
  return null;
}
function Menu(props) {
  return props.id ? /* @__PURE__ */ jsx3(MenuWithId, { ...props, id: props.id }) : /* @__PURE__ */ jsx3(BareMenu, { ...props });
}
function BareMenu(props) {
  return /* @__PURE__ */ jsx3(MenuWithId, { ...props, id: useId2() });
}
function MenuWithId({ label, items, icon, id }) {
  const triggerRef = useRef2(null);
  const menuRef = useRef2(null);
  const openAtPress = useRef2(null);
  const [open, setOpen] = useState3(false);
  const anchor = `--tny-anchor${id}`;
  function menuItems() {
    return [...menuRef.current?.querySelectorAll('[role="menuitem"]') ?? []];
  }
  function show(at) {
    menuRef.current?.togglePopover(true);
    const list = menuItems();
    (at === "first" ? list[0] : list.at(-1))?.focus();
  }
  function close() {
    menuRef.current?.togglePopover(false);
    triggerRef.current?.focus();
  }
  function onItemKeyDown(event) {
    const list = menuItems();
    const to = rovingIndex(event.key, list.indexOf(event.currentTarget), list.length, [
      "ArrowUp",
      "ArrowDown"
    ]);
    if (to !== null) {
      event.preventDefault();
      list[to]?.focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      close();
    } else if (event.key === " " && event.currentTarget instanceof HTMLAnchorElement) {
      event.preventDefault();
      event.currentTarget.click();
    }
  }
  function choose(item, event) {
    if (item.disabled) {
      event.preventDefault();
      return;
    }
    close();
    item.onSelect?.();
  }
  const trigger = {
    "aria-controls": `${id}-menu`,
    "aria-expanded": open,
    "aria-haspopup": "menu",
    id: `${id}-trigger`,
    onClick(event) {
      const pressed = event.detail > 0 ? openAtPress.current : null;
      openAtPress.current = null;
      if (pressed ?? open) menuRef.current?.togglePopover(false);
      else show("first");
    },
    onKeyDown(event) {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      event.preventDefault();
      show(event.key === "ArrowDown" ? "first" : "last");
    },
    onPointerDown() {
      openAtPress.current = open;
    },
    ref: triggerRef,
    style: { anchorName: anchor }
  };
  return /* @__PURE__ */ jsxs3(Fragment3, { children: [
    icon ? /* @__PURE__ */ jsx3(IconButton, { ...trigger, icon, label }) : /* @__PURE__ */ jsxs3(Button, { ...trigger, className: "tny-menu-trigger", variant: "secondary", children: [
      label,
      /* @__PURE__ */ jsx3("span", { "aria-hidden": "true", className: "tny-menu-chevron" })
    ] }),
    /* @__PURE__ */ jsx3(
      "div",
      {
        "aria-labelledby": `${id}-trigger`,
        className: "tny-menu",
        id: `${id}-menu`,
        onToggle: (event) => setOpen(event.newState === "open"),
        popover: "auto",
        ref: menuRef,
        role: "menu",
        style: { positionAnchor: anchor },
        children: items.map((item) => {
          const shared = {
            "aria-disabled": item.disabled || void 0,
            className: item.danger ? "tny-menu-item tny-menu-item-danger" : "tny-menu-item",
            onClick: (event) => choose(item, event),
            onKeyDown: onItemKeyDown,
            role: "menuitem",
            tabIndex: -1
          };
          return item.href !== void 0 ? /* @__PURE__ */ createElement("a", { ...shared, href: item.disabled ? void 0 : item.href, key: item.label }, item.label) : /* @__PURE__ */ createElement("button", { ...shared, key: item.label, type: "button" }, item.label);
        })
      }
    )
  ] });
}
var ToastContext = createContext(null);
var TOAST_MS = 5e3;
var MAX_TOASTS = 3;
function Toast({
  entry,
  paused,
  dismissLabel,
  onExpire,
  onDismiss
}) {
  useEffect(() => {
    if (paused || entry.tone === "error" || entry.action) return;
    const timer = setTimeout(() => onExpire(entry.id), TOAST_MS);
    return () => clearTimeout(timer);
  }, [entry, paused, onExpire]);
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className: `tny-toast tny-toast-${entry.tone}`,
      role: entry.tone === "error" ? "alert" : void 0,
      children: [
        /* @__PURE__ */ jsx3("span", { className: "tny-toast-icon", children: /* @__PURE__ */ jsx3(ToneIcon, { tone: entry.tone === "error" ? "danger" : entry.tone }) }),
        /* @__PURE__ */ jsx3("p", { className: "tny-toast-message", children: entry.message }),
        entry.action ? /* @__PURE__ */ jsx3(
          Button,
          {
            onClick: (event) => {
              entry.action?.onClick();
              onDismiss(entry.id, event);
            },
            variant: "ghost",
            children: entry.action.label
          }
        ) : null,
        /* @__PURE__ */ jsx3(
          IconButton,
          {
            icon: /* @__PURE__ */ jsx3(CrossIcon, {}),
            label: dismissLabel,
            onClick: (event) => onDismiss(entry.id, event)
          }
        )
      ]
    }
  );
}
function ToastProvider({
  children,
  label = "Notifications",
  dismissLabel = "Dismiss"
}) {
  const [toasts, setToasts] = useState3([]);
  const [hovered, setHovered] = useState3(false);
  const [focused, setFocused] = useState3(false);
  const lastId = useRef2(0);
  const regionRef = useRef2(null);
  const cameFrom = useRef2(null);
  const api = useMemo(
    () => ({
      toast({ message, tone = "info", action }) {
        lastId.current += 1;
        const entry = { id: lastId.current, message, tone, action };
        setToasts((list) => [...list, entry].slice(-MAX_TOASTS));
      }
    }),
    []
  );
  const remove = useCallback(
    (id) => setToasts((list) => list.filter((entry) => entry.id !== id)),
    []
  );
  const dismiss = useCallback(
    (id, event) => {
      const button = event.currentTarget;
      if (event.detail > 0) {
        setHovered(false);
      } else if (button === document.activeElement) {
        const toast = button.closest(".tny-toast");
        const buttons = [
          ...regionRef.current?.querySelectorAll(".tny-toast button") ?? []
        ];
        const at = buttons.indexOf(button);
        const others = (list) => list.filter((other) => !toast?.contains(other));
        (others(buttons.slice(at + 1))[0] ?? others(buttons.slice(0, at)).at(-1) ?? cameFrom.current)?.focus();
      }
      remove(id);
    },
    [remove]
  );
  useEffect(() => {
    const holdsFocus = toasts.length > 0 && regionRef.current?.contains(document.activeElement);
    if (focused && !holdsFocus) setFocused(false);
  }, [toasts, focused]);
  const toastFor = (entry) => /* @__PURE__ */ jsx3(
    Toast,
    {
      dismissLabel,
      entry,
      onDismiss: dismiss,
      onExpire: remove,
      paused: hovered || focused
    },
    entry.id
  );
  return /* @__PURE__ */ jsxs3(ToastContext, { value: api, children: [
    children,
    /* @__PURE__ */ jsxs3(
      "section",
      {
        "aria-label": label,
        className: "tny-toasts",
        onBlur: (event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
        },
        onFocus: (event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            cameFrom.current = event.relatedTarget instanceof HTMLElement ? event.relatedTarget : null;
          }
          setFocused(true);
        },
        onPointerEnter: () => setHovered(true),
        onPointerLeave: () => setHovered(false),
        ref: regionRef,
        children: [
          /* @__PURE__ */ jsx3("div", { className: "tny-toast-list", children: toasts.filter((entry) => entry.tone === "error").map(toastFor) }),
          /* @__PURE__ */ jsx3("div", { "aria-live": "polite", className: "tny-toast-list", children: toasts.filter((entry) => entry.tone !== "error").map(toastFor) })
        ]
      }
    )
  ] });
}
function useToast() {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast needs a ToastProvider above it.");
  return api;
}

// src/data.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var FILTERS_INLINE_QUERY = "(min-width: 48rem)";
function useFit(frameRef, tableRef) {
  const fit = useCallback2(() => {
    const frame = frameRef.current;
    const table = tableRef.current;
    if (!frame || !table) return;
    table.removeAttribute("data-fit");
    for (const fit2 of ["dense", "squeezed"]) {
      if (table.offsetWidth <= frame.clientWidth) return;
      table.setAttribute("data-fit", fit2);
    }
  }, [frameRef, tableRef]);
  useLayoutEffect2(fit);
  useEffect2(() => {
    if (typeof ResizeObserver !== "function") return;
    let pending = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(pending);
      pending = requestAnimationFrame(fit);
    });
    for (const box of [frameRef.current, tableRef.current]) if (box) observer.observe(box);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(pending);
    };
  }, [fit, frameRef, tableRef]);
}
var SKELETON_ROWS = ["a", "b", "c", "d", "e"];
var ARIA_SORT = { asc: "ascending", desc: "descending" };
function columnClass({ priority, align }) {
  return cx(
    priority === 2 && "tny-col-p2",
    priority === 3 && "tny-col-p3",
    align === "end" && "tny-align-end"
  );
}
function SortIcon({ direction }) {
  return /* @__PURE__ */ jsxs4(
    "svg",
    {
      "aria-hidden": "true",
      className: "tny-table-sort-icon",
      fill: "none",
      height: "1em",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "2",
      viewBox: "0 0 24 24",
      width: "1em",
      children: [
        direction === "desc" ? null : /* @__PURE__ */ jsx4("path", { d: "M8 10l4-4 4 4" }),
        direction === "asc" ? null : /* @__PURE__ */ jsx4("path", { d: "M8 14l4 4 4-4" })
      ]
    }
  );
}
function spokenText(element) {
  if (!element) return "";
  let text = element.innerText;
  if (typeof text !== "string") {
    const parts = [];
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) parts.push(walker.currentNode.textContent ?? "");
    text = parts.join(" ");
  }
  return text.replace(/\s+/g, " ").trim();
}
function Table({
  caption,
  captionHidden = false,
  columns,
  rows,
  rowKey,
  loading = false,
  loadingLabel = "Loading\u2026",
  error,
  empty,
  sort,
  onSortChange,
  emptyCell = /* @__PURE__ */ jsx4(Muted, { children: "\u2014" })
}) {
  const slotRef = useRef3(null);
  const frameRef = useRef3(null);
  const tableRef = useRef3(null);
  useFit(frameRef, tableRef);
  const sized = columns.some((column) => column.width);
  const [emptyText, setEmptyText] = useState4("");
  const showEmpty = !loading && !error && rows.length === 0 && Boolean(empty);
  useLayoutEffect2(() => {
    setEmptyText(showEmpty ? spokenText(slotRef.current) : "");
  });
  let body;
  if (loading) {
    body = SKELETON_ROWS.map((key) => (
      // biome-ignore lint/a11y/noAriaHiddenOnFocusable: a row of a static table is not focusable (Biome counts the row role as a widget, for grids). The placeholder rows are decorative, and the table is aria-busy meanwhile.
      /* @__PURE__ */ jsx4("tr", { "aria-hidden": "true", className: "tny-table-row", role: "row", children: columns.map((column) => /* @__PURE__ */ jsx4(
        "td",
        {
          className: cx("tny-table-cell", columnClass(column)),
          "data-label": column.header,
          role: "cell",
          children: /* @__PURE__ */ jsx4(
            "span",
            {
              className: cx(
                "tny-table-skeleton",
                column.skeleton === "action" && "tny-table-skeleton-action"
              )
            }
          )
        },
        column.key
      )) }, key)
    ));
  } else if (error || rows.length === 0 && empty) {
    body = /* @__PURE__ */ jsx4("tr", { className: "tny-table-row", role: "row", children: /* @__PURE__ */ jsx4(
      "td",
      {
        className: "tny-table-cell tny-table-slot",
        colSpan: columns.length,
        ref: slotRef,
        role: "cell",
        children: error ? /* @__PURE__ */ jsx4("div", { role: "alert", children: error }) : empty
      }
    ) });
  } else {
    body = rows.map((row) => /* @__PURE__ */ jsx4("tr", { className: "tny-table-row", role: "row", children: columns.map((column, index) => {
      const className = cx("tny-table-cell", columnClass(column));
      const value = column.cell(row);
      const content = value === null || value === void 0 || value === "" ? emptyCell : value;
      return index === 0 ? /* @__PURE__ */ jsx4(
        "th",
        {
          className,
          "data-label": column.header,
          role: "rowheader",
          scope: "row",
          children: content
        },
        column.key
      ) : /* @__PURE__ */ jsx4("td", { className, "data-label": column.header, role: "cell", children: content }, column.key);
    }) }, rowKey(row)));
  }
  return /* @__PURE__ */ jsxs4("div", { className: "tny-table-frame", ref: frameRef, children: [
    /* @__PURE__ */ jsx4("div", { className: "tny-visually-hidden", role: "status", children: (loading ? loadingLabel : emptyText) || null }),
    /* @__PURE__ */ jsxs4(
      "table",
      {
        "aria-busy": loading || void 0,
        className: cx("tny-table", sized && "tny-table-fixed"),
        ref: tableRef,
        role: "table",
        children: [
          /* @__PURE__ */ jsx4("caption", { className: cx("tny-table-caption", captionHidden && "tny-visually-hidden"), children: caption }),
          sized ? /* @__PURE__ */ jsx4("colgroup", { children: columns.map((column) => /* @__PURE__ */ jsx4(
            "col",
            {
              className: column.priority === 3 ? "tny-table-col-p3" : void 0,
              style: column.width ? { width: column.width } : void 0
            },
            column.key
          )) }) : null,
          /* @__PURE__ */ jsx4("thead", { role: "rowgroup", children: /* @__PURE__ */ jsx4("tr", { role: "row", children: columns.map((column) => {
            const sorted = sort?.key === column.key ? sort.direction : void 0;
            return /* @__PURE__ */ jsxs4(
              "th",
              {
                "aria-sort": sorted && ARIA_SORT[sorted],
                className: cx(
                  "tny-table-head",
                  column.sortable && "tny-table-head-sortable",
                  column.headerControl != null && "tny-table-head-control",
                  columnClass(column)
                ),
                role: "columnheader",
                scope: "col",
                children: [
                  column.headerControl,
                  column.sortable ? /* @__PURE__ */ jsxs4(
                    "button",
                    {
                      className: "tny-table-sort",
                      onClick: () => onSortChange?.({
                        key: column.key,
                        direction: sorted === "asc" ? "desc" : "asc"
                      }),
                      type: "button",
                      children: [
                        column.header,
                        /* @__PURE__ */ jsx4(SortIcon, { direction: sorted })
                      ]
                    }
                  ) : column.header
                ]
              },
              column.key
            );
          }) }) }),
          /* @__PURE__ */ jsx4("tbody", { role: "rowgroup", children: body })
        ]
      }
    )
  ] });
}
function Pagination({
  summary,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  label = "Pagination",
  previousLabel = "Previous",
  nextLabel = "Next"
}) {
  return /* @__PURE__ */ jsxs4("nav", { "aria-label": label, className: "tny-pagination", children: [
    /* @__PURE__ */ jsx4("p", { "aria-atomic": "true", "aria-live": "polite", className: "tny-pagination-summary", children: summary }),
    /* @__PURE__ */ jsxs4("div", { className: "tny-pagination-buttons", children: [
      /* @__PURE__ */ jsx4(
        Button,
        {
          "aria-disabled": !hasPrevious || void 0,
          onClick: hasPrevious ? () => onPrevious() : void 0,
          variant: "secondary",
          children: previousLabel
        }
      ),
      /* @__PURE__ */ jsx4(
        Button,
        {
          "aria-disabled": !hasNext || void 0,
          onClick: hasNext ? () => onNext() : void 0,
          variant: "secondary",
          children: nextLabel
        }
      )
    ] })
  ] });
}
function FilterBar({
  label,
  activeCount,
  onClear,
  children,
  clearLabel = "Clear filters",
  summaryLabel = (count) => count > 0 ? `Filters (${count})` : "Filters"
}) {
  const fieldsRef = useRef3(null);
  const [open, setOpen] = useState4(
    () => typeof window.matchMedia !== "function" || window.matchMedia(FILTERS_INLINE_QUERY).matches
  );
  useEffect2(() => {
    if (typeof window.matchMedia !== "function") return;
    const inline = window.matchMedia(FILTERS_INLINE_QUERY);
    const onChange = () => {
      if (inline.matches) setOpen(true);
    };
    inline.addEventListener("change", onChange);
    return () => inline.removeEventListener("change", onChange);
  }, []);
  function clear() {
    onClear();
    const fields = fieldsRef.current;
    if (fields) tabStops(fields)[0]?.focus();
  }
  return (
    // biome-ignore lint/a11y/useSemanticElements: browsers before 2023, and the aria-query 5.3 that Testing Library uses, do not map <search> to the search landmark. The role works everywhere.
    /* @__PURE__ */ jsx4("div", { "aria-label": label, className: "tny-filterbar", role: "search", children: /* @__PURE__ */ jsxs4(
      "details",
      {
        className: "tny-filterbar-details",
        onToggle: (event) => setOpen(event.currentTarget.open),
        open,
        children: [
          /* @__PURE__ */ jsxs4("summary", { className: "tny-filterbar-summary", children: [
            summaryLabel(activeCount),
            /* @__PURE__ */ jsx4("span", { "aria-hidden": "true", className: "tny-filterbar-chevron" })
          ] }),
          /* @__PURE__ */ jsx4("div", { className: "tny-filterbar-body", children: /* @__PURE__ */ jsxs4("div", { className: "tny-filterbar-fields", ref: fieldsRef, children: [
            children,
            activeCount > 0 ? /* @__PURE__ */ jsx4(Button, { className: "tny-filterbar-clear", onClick: clear, variant: "ghost", children: clearLabel }) : null
          ] }) })
        ]
      }
    ) })
  );
}
function SearchIcon() {
  return /* @__PURE__ */ jsxs4(
    "svg",
    {
      "aria-hidden": "true",
      className: "tny-search-icon",
      fill: "none",
      height: "1em",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeWidth: "2",
      viewBox: "0 0 24 24",
      width: "1em",
      children: [
        /* @__PURE__ */ jsx4("circle", { cx: "11", cy: "11", r: "7" }),
        /* @__PURE__ */ jsx4("path", { d: "M20 20l-3.5-3.5" })
      ]
    }
  );
}
function SearchInput({
  label,
  value,
  onSearch,
  delayMs = 300,
  clearLabel = "Clear",
  maxLength,
  placeholder,
  disabled
}) {
  const id = useId3();
  const inputRef = useRef3(null);
  const [text, setText] = useState4(value);
  const [searched, setSearched] = useState4(value);
  const sent = useRef3([]);
  useLayoutEffect2(() => {
    const echo = sent.current.findIndex((each) => each === value || each.trim() === value);
    if (echo >= 0) {
      sent.current = sent.current.slice(echo + 1);
      return;
    }
    sent.current = [];
    setText(value);
    setSearched(value);
  }, [value]);
  function search(next) {
    sent.current.push(next);
    setSearched(next);
    onSearch(next);
  }
  const searchTyped = useEffectEvent2(() => search(text));
  useEffect2(() => {
    if (text === searched) return;
    const timer = setTimeout(() => searchTyped(), delayMs);
    return () => clearTimeout(timer);
  }, [text, searched, delayMs]);
  function onKeyDown(event) {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    search(event.currentTarget.value);
  }
  function clear() {
    setText("");
    search("");
    inputRef.current?.focus();
  }
  return /* @__PURE__ */ jsxs4("div", { className: "tny-field tny-search", children: [
    /* @__PURE__ */ jsx4("label", { className: "tny-label", htmlFor: id, children: label }),
    /* @__PURE__ */ jsxs4("div", { className: "tny-search-box", children: [
      /* @__PURE__ */ jsx4(SearchIcon, {}),
      /* @__PURE__ */ jsx4(
        Input,
        {
          autoComplete: "off",
          className: "tny-search-input",
          disabled,
          enterKeyHint: "search",
          id,
          maxLength,
          onChange: (event) => setText(event.currentTarget.value),
          onKeyDown,
          placeholder,
          ref: inputRef,
          type: "search",
          value: text
        }
      ),
      text ? /* @__PURE__ */ jsx4(
        IconButton,
        {
          className: "tny-search-clear",
          icon: /* @__PURE__ */ jsx4(CrossIcon, {}),
          label: clearLabel,
          onClick: clear,
          size: "sm"
        }
      ) : null
    ] })
  ] });
}

// src/form-wizard.tsx
import {
  useEffect as useEffect4,
  useEffectEvent as useEffectEvent3,
  useId as useId5,
  useLayoutEffect as useLayoutEffect4,
  useRef as useRef5,
  useState as useState6
} from "react";

// src/navigation.tsx
import {
  useCallback as useCallback3,
  useEffect as useEffect3,
  useId as useId4,
  useLayoutEffect as useLayoutEffect3,
  useRef as useRef4,
  useState as useState5
} from "react";
import { Fragment as Fragment4, jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var SIDEBAR_QUERY = "(min-width: 64rem)";
function useOverflowEdges(ref) {
  const update = useCallback3(() => {
    const bar = ref.current;
    if (!bar) return;
    const end = bar.scrollWidth - bar.clientWidth - bar.scrollLeft;
    bar.toggleAttribute("data-overflow-start", bar.scrollLeft > 1);
    bar.toggleAttribute("data-overflow-end", end > 1);
  }, [ref]);
  useLayoutEffect3(update);
  useEffect3(() => {
    const bar = ref.current;
    if (!bar) return;
    bar.addEventListener("scroll", update, { passive: true });
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(update) : null;
    for (const box of [bar, ...bar.children]) observer?.observe(box);
    return () => {
      bar.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [ref, update]);
}
function Tabs({ label, tabs, value, onChange }) {
  const id = useId4();
  const listRef = useRef4(null);
  useOverflowEdges(listRef);
  const selected = tabs.some((tab) => tab.id === value) ? value : tabs[0]?.id;
  function onKeyDown(event, index) {
    const to = rovingIndex(event.key, index, tabs.length, ["ArrowLeft", "ArrowRight"]);
    const tab = to === null ? void 0 : tabs[to];
    if (to === null || !tab) return;
    event.preventDefault();
    onChange(tab.id);
    const button = listRef.current?.children[to];
    if (button instanceof HTMLElement) button.focus();
  }
  return /* @__PURE__ */ jsxs5("div", { className: "tny-tabs", children: [
    /* @__PURE__ */ jsx5("div", { "aria-label": label, className: "tny-tablist", ref: listRef, role: "tablist", children: tabs.map((tab, index) => /* @__PURE__ */ jsxs5(
      "button",
      {
        "aria-controls": `${id}-panel-${tab.id}`,
        "aria-selected": tab.id === selected,
        className: "tny-tab",
        id: `${id}-tab-${tab.id}`,
        onClick: () => onChange(tab.id),
        onKeyDown: (event) => onKeyDown(event, index),
        role: "tab",
        tabIndex: tab.id === selected ? 0 : -1,
        type: "button",
        children: [
          tab.label,
          tab.badge ? /* @__PURE__ */ jsxs5(Fragment4, { children: [
            " ",
            /* @__PURE__ */ jsx5(Badge, { children: tab.badge })
          ] }) : null
        ]
      },
      tab.id
    )) }),
    tabs.map((tab) => /* @__PURE__ */ jsx5(
      "div",
      {
        "aria-labelledby": `${id}-tab-${tab.id}`,
        className: "tny-tabpanel",
        hidden: tab.id !== selected,
        id: `${id}-panel-${tab.id}`,
        role: "tabpanel",
        tabIndex: 0,
        children: tab.content
      },
      tab.id
    ))
  ] });
}
function NavTabs({ label, children }) {
  const ref = useRef4(null);
  useOverflowEdges(ref);
  const scrolledTo = useRef4(null);
  useLayoutEffect3(() => {
    const nav = ref.current;
    const current = nav?.querySelector(':scope > [aria-current="page"]');
    const href = current?.getAttribute("href") ?? null;
    if (href === scrolledTo.current) return;
    scrolledTo.current = href;
    if (!nav || !current) return;
    const centred = current.offsetLeft + current.offsetWidth / 2 - nav.clientWidth / 2;
    nav.scrollLeft = Math.max(0, Math.min(centred, nav.scrollWidth - nav.clientWidth));
  });
  return /* @__PURE__ */ jsx5("nav", { "aria-label": label, className: "tny-navtabs", ref, children });
}
function MenuIcon() {
  return /* @__PURE__ */ jsx5(
    "svg",
    {
      "aria-hidden": "true",
      fill: "none",
      height: "1em",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeWidth: "2",
      viewBox: "0 0 24 24",
      width: "1em",
      children: /* @__PURE__ */ jsx5("path", { d: "M4 7h16M4 12h16M4 17h16" })
    }
  );
}
function AppShell({
  brand,
  nav,
  navLabel,
  footer,
  children,
  menuLabel = "Menu",
  closeLabel = "Close"
}) {
  const id = useId4();
  const [open, setOpen] = useState5(false);
  const menuRef = useRef4(null);
  const closeRef = useRef4(null);
  const modal = useModal({ open, onClose: () => setOpen(false), closeRef, returnFocusRef: menuRef });
  useEffect3(() => {
    if (typeof window.matchMedia !== "function") return;
    const wide = window.matchMedia(SIDEBAR_QUERY);
    const onChange = () => {
      if (wide.matches) setOpen(false);
    };
    wide.addEventListener("change", onChange);
    return () => wide.removeEventListener("change", onChange);
  }, []);
  function closeOnBackdropOrLink(event) {
    const followedLink = event.target instanceof Element && event.target.closest("a[href]");
    if (event.target === event.currentTarget || followedLink) setOpen(false);
  }
  const navigation = /* @__PURE__ */ jsxs5(Fragment4, { children: [
    /* @__PURE__ */ jsx5("nav", { "aria-label": navLabel, className: "tny-shell-nav", children: nav }),
    footer ? /* @__PURE__ */ jsx5("div", { className: "tny-shell-footer", children: footer }) : null
  ] });
  return /* @__PURE__ */ jsxs5("div", { className: "tny-shell", children: [
    /* @__PURE__ */ jsxs5("header", { className: "tny-shell-header", children: [
      /* @__PURE__ */ jsx5(
        IconButton,
        {
          "aria-controls": `${id}-drawer`,
          "aria-expanded": open,
          className: "tny-shell-menu",
          icon: /* @__PURE__ */ jsx5(MenuIcon, {}),
          label: menuLabel,
          onClick: () => setOpen(true),
          ref: menuRef
        }
      ),
      /* @__PURE__ */ jsx5("div", { className: "tny-shell-brand", children: brand }),
      /* @__PURE__ */ jsx5("div", { className: "tny-shell-sidebar", children: open ? null : navigation })
    ] }),
    /* @__PURE__ */ jsx5(
      "dialog",
      {
        "aria-label": navLabel,
        className: "tny-shell-drawer",
        id: `${id}-drawer`,
        onCancel: modal.onCancel,
        onClick: closeOnBackdropOrLink,
        onKeyDown: modal.onKeyDown,
        ref: modal.ref,
        children: open ? /* @__PURE__ */ jsxs5("div", { className: "tny-shell-drawer-panel", children: [
          /* @__PURE__ */ jsxs5("div", { className: "tny-shell-drawer-header", children: [
            /* @__PURE__ */ jsx5(
              IconButton,
              {
                className: "tny-shell-close",
                icon: /* @__PURE__ */ jsx5(CrossIcon, {}),
                label: closeLabel,
                onClick: () => setOpen(false),
                ref: closeRef
              }
            ),
            /* @__PURE__ */ jsx5("div", { className: "tny-shell-brand", children: brand })
          ] }),
          navigation
        ] }) : null
      }
    ),
    /* @__PURE__ */ jsx5("main", { className: "tny-shell-main", children })
  ] });
}

// src/form-wizard.tsx
import { Fragment as Fragment5, jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var WizardFieldError = class extends Error {
  constructor(stepId, errors) {
    super(Object.values(errors).filter(Boolean).join(" "));
    this.stepId = stepId;
    this.errors = errors;
  }
  stepId;
  errors;
  name = "WizardFieldError";
};
var DEFAULT_LABELS = {
  back: "Back",
  next: "Continue",
  completed: "completed",
  stepOf: (current, total, title) => `Step ${current} of ${total}: ${title}`
};
function CheckIcon() {
  return /* @__PURE__ */ jsx6(
    "svg",
    {
      "aria-hidden": "true",
      fill: "none",
      height: "1em",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "3",
      viewBox: "0 0 24 24",
      width: "1em",
      children: /* @__PURE__ */ jsx6("path", { d: "M5 12.5l4.5 4.5L19 7.5" })
    }
  );
}
function Stepper({
  label,
  steps,
  currentId,
  completedIds = [],
  onSelect,
  completedLabel = DEFAULT_LABELS.completed,
  stepOf = DEFAULT_LABELS.stepOf,
  allDone = (total) => `All ${total} steps done`
}) {
  const at = currentId === void 0 ? steps.length : Math.max(
    steps.findIndex((step) => step.id === currentId),
    0
  );
  const current = steps[at];
  return /* @__PURE__ */ jsxs6("div", { className: "tny-stepper", children: [
    /* @__PURE__ */ jsx6("ol", { "aria-label": label, className: "tny-stepper-list", children: steps.map((step, index) => {
      const isCurrent = index === at;
      const done = !isCurrent && completedIds.includes(step.id);
      const content = /* @__PURE__ */ jsxs6(Fragment5, { children: [
        /* @__PURE__ */ jsx6("span", { "aria-hidden": "true", className: "tny-stepper-marker", children: done ? /* @__PURE__ */ jsx6(CheckIcon, {}) : index + 1 }),
        /* @__PURE__ */ jsx6("span", { className: "tny-stepper-label", children: step.label }),
        done ? /* @__PURE__ */ jsxs6("span", { className: "tny-visually-hidden", children: [
          ", ",
          completedLabel
        ] }) : null
      ] });
      return /* @__PURE__ */ jsx6(
        "li",
        {
          "aria-current": isCurrent ? "step" : void 0,
          className: done ? "tny-stepper-item tny-stepper-item-done" : "tny-stepper-item",
          children: done && onSelect ? /* @__PURE__ */ jsx6(
            "button",
            {
              className: "tny-stepper-step tny-stepper-button",
              onClick: () => onSelect(step.id),
              type: "button",
              children: content
            }
          ) : /* @__PURE__ */ jsx6("span", { className: "tny-stepper-step", children: content })
        },
        step.id
      );
    }) }),
    /* @__PURE__ */ jsxs6("div", { className: "tny-stepper-summary", children: [
      /* @__PURE__ */ jsx6("p", { className: "tny-stepper-summary-text", children: current ? stepOf(at + 1, steps.length, current.label) : allDone(steps.length) }),
      /* @__PURE__ */ jsx6("div", { "aria-hidden": "true", className: "tny-stepper-bar", children: /* @__PURE__ */ jsx6(
        "div",
        {
          className: "tny-stepper-bar-fill",
          style: { width: `${Math.min(at + 1, steps.length) / steps.length * 100}%` }
        }
      ) })
    ] })
  ] });
}
function hasErrors(errors) {
  return Object.values(errors).some(Boolean);
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function landing(steps, shown, id) {
  const at = steps.findIndex((step) => step.id === id);
  if (at < 0) return 0;
  const next = shown.findIndex((step) => steps.indexOf(step) >= at);
  return next < 0 ? shown.length - 1 : next;
}
function clamp(shown, index, draft) {
  const invalid = shown.slice(0, index).findIndex((step) => hasErrors(step.validate?.(draft) ?? {}));
  return invalid < 0 ? index : invalid;
}
function snapshotOf(step, value) {
  return JSON.stringify(step.fields ? step.fields.map((key) => value[key]) : value);
}
function readStorage(key) {
  if (!key) return void 0;
  try {
    return JSON.parse(sessionStorage.getItem(key) ?? "null");
  } catch {
    return void 0;
  }
}
function writeStorage(key, value) {
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
  }
}
function removeStorage(key) {
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
  }
}
function FormWizard({
  title,
  steps,
  initialDraft,
  mode = "steps",
  stepId,
  initialStepTrusted = false,
  onStepChange,
  onStepSubmit,
  onSkip,
  submitLabel,
  onSubmit,
  onCancel,
  headingLevel = 1,
  showStepper = true,
  announce = true,
  titleHidden = false,
  persistKey,
  labels,
  renderError
}) {
  const id = useId5();
  const {
    back: backLabel,
    completed: completedLabel,
    next: nextLabel,
    stepOf
  } = { ...DEFAULT_LABELS, ...labels };
  const submittedKey = persistKey ? `${persistKey}:submitted` : void 0;
  const [initial] = useState6(initialDraft);
  const [draft, setDraftState] = useState6(() => {
    const stored = readStorage(persistKey);
    return isRecord(stored) ? { ...initialDraft, ...stored } : initialDraft;
  });
  const [submitted, setSubmitted] = useState6(() => {
    const stored = readStorage(submittedKey);
    return isRecord(stored) ? stored : {};
  });
  const [current, setCurrent] = useState6(() => {
    const applicable = steps.filter((step2) => !step2.skip?.(draft));
    const to = landing(steps, applicable, stepId);
    return applicable[initialStepTrusted ? to : clamp(applicable, to, draft)]?.id ?? "";
  });
  const [syncedStepId, setSyncedStepId] = useState6(stepId);
  const [errors, setErrors] = useState6({});
  const [formError, setFormError] = useState6("");
  const [formErrorBy, setFormErrorBy] = useState6("primary");
  const [pending, setPending] = useState6(null);
  const [focusRequest, setFocusRequest] = useState6(null);
  const pendingRef = useRef5(false);
  const formRef = useRef5(null);
  const headingRef = useRef5(null);
  const primaryRef = useRef5(null);
  const skipRef = useRef5(null);
  const tabs = mode === "tabs";
  const shown = steps.filter((step2) => !step2.skip?.(draft));
  const index = landing(steps, shown, current);
  const step = shown[index];
  if (stepId !== syncedStepId) {
    setSyncedStepId(stepId);
    let to = stepId === void 0 ? index : landing(steps, shown, stepId);
    if (to > index) to = clamp(shown, to, draft);
    const target = shown[to];
    if (target && target.id !== step?.id) {
      setCurrent(target.id);
      setErrors({});
      setFormError("");
      setFocusRequest({ to: "heading" });
    }
  }
  const reportLanding = useEffectEvent3((requested) => {
    if (requested !== void 0 && step && requested !== step.id) onStepChange?.(step.id);
  });
  useEffect4(() => reportLanding(stepId), [stepId]);
  useEffect4(() => writeStorage(persistKey, draft), [draft, persistKey]);
  useEffect4(() => {
    if (Object.keys(submitted).length > 0) writeStorage(submittedKey, submitted);
    else removeStorage(submittedKey);
  }, [submitted, submittedKey]);
  useLayoutEffect4(() => {
    const form = formRef.current;
    const dialog = form?.closest("dialog");
    const titleId2 = dialog?.getAttribute("aria-labelledby");
    if (!form || !dialog?.open || !titleId2) return;
    if (document.activeElement !== document.getElementById(titleId2)) return;
    const stops = tabStops(form);
    (stops.find((stop) => stop.closest(".tny-wizard-body")) ?? stops[0])?.focus();
  }, []);
  useLayoutEffect4(() => {
    if (!focusRequest) return;
    const form = formRef.current;
    const fallback = headingRef.current ?? form?.querySelector('[role="tabpanel"]:not([hidden])');
    let target = fallback;
    if (focusRequest.to === "primary") target = primaryRef.current;
    if (focusRequest.to === "skip") target = skipRef.current ?? primaryRef.current;
    if (focusRequest.to === "invalid") {
      const invalid = [...form?.querySelectorAll('[aria-invalid="true"]') ?? []];
      target = invalid.find((field) => !field.closest("[hidden]")) ?? fallback;
    }
    target?.focus();
  }, [focusRequest]);
  if (!step) throw new Error("FormWizard needs at least one step that applies to the draft.");
  const last = index === shown.length - 1;
  const show = (target, options = {}) => {
    let to = landing(steps, shown, target);
    if (options.clampForward && to > index) to = clamp(shown, to, draft);
    const next2 = shown[to];
    if (!next2) return;
    setCurrent(next2.id);
    setErrors(options.errors ?? {});
    setFormError("");
    setFocusRequest({ to: options.focus ?? "heading" });
    if (next2.id !== step.id) onStepChange?.(next2.id);
  };
  const dropSubmit = (stepId2) => setSubmitted(
    (map) => stepId2 in map ? Object.fromEntries(Object.entries(map).filter(([key]) => key !== stepId2)) : map
  );
  const attempt = async (by, task) => {
    pendingRef.current = true;
    setPending(by);
    setFormError("");
    try {
      return { value: await task() };
    } catch (error) {
      if (error instanceof WizardFieldError && shown.some((each) => each.id === error.stepId)) {
        dropSubmit(error.stepId);
        show(error.stepId, { errors: error.errors, focus: "invalid" });
      } else {
        setFormError(error instanceof Error ? error.message : String(error));
        setFormErrorBy(by);
        setFocusRequest({ to: by });
      }
      return void 0;
    } finally {
      pendingRef.current = false;
      setPending(null);
    }
  };
  const finish = async (value, by) => {
    setErrors({});
    const done = await attempt(by, () => onSubmit(value));
    if (done) {
      removeStorage(persistKey);
      removeStorage(submittedKey);
    }
    return Boolean(done);
  };
  const next = async () => {
    const stepErrors = step.validate?.(draft) ?? {};
    if (hasErrors(stepErrors)) {
      setErrors(stepErrors);
      setFormError("");
      setFocusRequest({ to: "invalid" });
      return;
    }
    const following = shown[index + 1];
    if (!following) {
      await finish(draft, "primary");
      return;
    }
    if (onStepSubmit && submitted[step.id] !== snapshotOf(step, draft)) {
      const sent = draft;
      const outcome = await attempt("primary", () => onStepSubmit(step.id, sent));
      if (!outcome) return;
      const result = outcome.value || void 0;
      if (result) setDraftState((latest) => ({ ...latest, ...result }));
      const snapshot = snapshotOf(step, result ? { ...sent, ...result } : sent);
      setSubmitted((map) => ({ ...map, [step.id]: snapshot }));
    }
    show(following.id);
  };
  const withoutStep = (value) => step.fields ? { ...value, ...Object.fromEntries(step.fields.map((key) => [key, initial[key]])) } : value;
  const skipStep = async () => {
    if (pendingRef.current) return;
    const cleared = withoutStep(draft);
    if (onSkip && !await attempt("skip", () => onSkip(step.id, cleared))) return;
    const following = shown[index + 1];
    if (following) {
      setDraftState(withoutStep);
      dropSubmit(step.id);
      show(following.id);
    } else if (!await finish(cleared, "skip")) {
      setDraftState(withoutStep);
      dropSubmit(step.id);
    }
  };
  const save = async () => {
    const all = shown.map((each) => each.validate?.(draft) ?? {});
    const invalid = shown.findIndex((_, at) => hasErrors(all[at] ?? {}));
    const target = shown[invalid];
    const merged = Object.fromEntries(
      all.flatMap((each) => Object.entries(each).filter(([, m]) => m))
    );
    if (target) show(target.id, { errors: merged, focus: "invalid" });
    else await finish(draft, "primary");
  };
  const onFormSubmit = (event) => {
    event.preventDefault();
    if (pendingRef.current) return;
    void (tabs ? save() : next());
  };
  const back = () => {
    if (pendingRef.current) return;
    const previous = tabs ? void 0 : shown[index - 1];
    if (previous) show(previous.id);
    else onCancel?.();
  };
  const goTo = (target) => {
    if (!pendingRef.current) show(target, { clampForward: true });
  };
  const selectTab = (target) => {
    setCurrent(target);
    if (target !== step.id) onStepChange?.(target);
  };
  const setDraft = (update) => setDraftState(
    (previous) => typeof update === "function" ? update(previous) : { ...previous, ...update }
  );
  const context = { draft, errors, setDraft, goTo };
  const Title = headingLevel === 2 ? "h2" : "h1";
  const StepTitle = headingLevel === 2 ? "h3" : "h2";
  const titleId = `${id}-title`;
  const hasBack = tabs ? Boolean(onCancel) : index > 0 || Boolean(onCancel);
  const stepperShown = showStepper && !tabs;
  return /* @__PURE__ */ jsxs6("div", { className: "tny-wizard", children: [
    /* @__PURE__ */ jsxs6("div", { className: "tny-wizard-header", children: [
      /* @__PURE__ */ jsx6(
        Title,
        {
          className: cx(
            "tny-wizard-title",
            headingLevel === 2 && "tny-wizard-title-small",
            titleHidden && "tny-visually-hidden"
          ),
          id: titleId,
          children: title
        }
      ),
      stepperShown ? /* @__PURE__ */ jsx6(
        Stepper,
        {
          completedIds: shown.slice(0, index).map((each) => each.id),
          completedLabel,
          currentId: step.id,
          label: title,
          onSelect: goTo,
          stepOf,
          steps: shown.map((each) => ({ id: each.id, label: each.title }))
        }
      ) : null
    ] }),
    /* @__PURE__ */ jsxs6(
      "form",
      {
        "aria-labelledby": titleId,
        className: "tny-wizard-form",
        noValidate: true,
        onSubmit: onFormSubmit,
        ref: formRef,
        children: [
          tabs ? /* @__PURE__ */ jsx6(
            Tabs,
            {
              label: title,
              onChange: selectTab,
              tabs: shown.map((each) => ({
                id: each.id,
                label: each.title,
                content: /* @__PURE__ */ jsxs6("div", { className: "tny-wizard-body", children: [
                  each.description ? /* @__PURE__ */ jsx6("p", { className: "tny-wizard-step-description", children: each.description }) : null,
                  each.render(context)
                ] })
              })),
              value: step.id
            }
          ) : (
            // Keyed, so a step never inherits the previous step's uncontrolled inputs.
            /* @__PURE__ */ jsxs6("div", { className: "tny-wizard-body", children: [
              /* @__PURE__ */ jsxs6("div", { className: "tny-wizard-step-header", children: [
                /* @__PURE__ */ jsx6(
                  StepTitle,
                  {
                    className: cx("tny-wizard-step-title", stepperShown && "tny-visually-hidden"),
                    ref: headingRef,
                    tabIndex: -1,
                    children: step.title
                  }
                ),
                step.description ? /* @__PURE__ */ jsx6("p", { className: "tny-wizard-step-description", children: step.description }) : null
              ] }),
              step.render(context)
            ] }, step.id)
          ),
          formError ? renderError ? renderError(formError, {
            goTo,
            retry: () => ((formErrorBy === "skip" ? skipRef.current : null) ?? primaryRef.current)?.click()
          }) : /* @__PURE__ */ jsx6("p", { className: "tny-error", role: "alert", children: formError }) : null,
          /* @__PURE__ */ jsxs6("div", { className: "tny-wizard-footer", children: [
            hasBack ? /* @__PURE__ */ jsx6(
              Button,
              {
                className: "tny-wizard-back",
                disabled: pending !== null,
                onClick: back,
                variant: "secondary",
                children: backLabel
              }
            ) : null,
            !tabs && step.skipLabel ? /* @__PURE__ */ jsx6(
              Button,
              {
                disabled: pending === "primary",
                loading: pending === "skip",
                onClick: () => void skipStep(),
                ref: skipRef,
                variant: "ghost",
                children: step.skipLabel
              }
            ) : null,
            tabs && step.readOnly ? null : /* @__PURE__ */ jsx6(
              Button,
              {
                disabled: pending === "skip",
                loading: pending === "primary",
                ref: primaryRef,
                type: "submit",
                variant: tabs ? "primary" : (typeof step.nextVariant === "function" ? step.nextVariant(draft) : step.nextVariant) ?? "primary",
                children: tabs || last ? submitLabel : step.nextLabel ?? nextLabel
              }
            )
          ] })
        ]
      }
    ),
    tabs || !announce ? null : /* @__PURE__ */ jsx6("p", { "aria-live": "polite", className: "tny-visually-hidden", children: stepOf(index + 1, shown.length, step.title) })
  ] });
}

// src/theme.ts
import { createElement as createElement2 } from "react";
var DEFAULT_BRAND = "#176b51";
var WHITE = "#ffffff";
var BLACK = "#000000";
var INK = "#111318";
var STEP = 0.02;
var MAX_STEPS = 50;
var DARK_TEXT_STEPS = 100;
var DARK_MAX_LIGHTNESS = 0.75;
var DARK_MAX_CHROMA = 0.14;
var HOVER_STEPS = 6;
var SCRIM_SHOW_THROUGH = 0.12;
var NEUTRALS = {
  light: {
    bg: "#f8f9fa",
    surface: "#ffffff",
    text: INK,
    muted: "#5c6370",
    border: "#858b96",
    danger: "#b42318",
    onDanger: WHITE,
    // A danger button: a fill under white text, darker on hover (#b42318 mixed 15% towards INK).
    dangerFill: "#b42318",
    onDangerFill: WHITE,
    dangerFillHover: "#9c2118",
    success: "#137a3a"
  },
  dark: {
    bg: "#0e1013",
    surface: "#16181d",
    text: "#f3f4f6",
    muted: "#9ca3af",
    border: "#6b7280",
    danger: "#f97066",
    onDanger: INK,
    // Lighter on hover (#c4372c mixed 10% towards white): a darker red falls under 3:1 against the
    // dark surface, and a fill identifies the button, so it keeps 3:1 while hovered.
    dangerFill: "#c4372c",
    onDangerFill: WHITE,
    dangerFillHover: "#ca4b41",
    success: "#4ade80"
  }
};
var BRAND_PROPERTIES = {
  primary: "primary",
  primaryHover: "primary-hover",
  onPrimary: "on-primary",
  primaryText: "primary-text",
  primaryScrim: "primary-scrim",
  focusRing: "focus-ring"
};
function normaliseHex(hex) {
  const digits = /^#([0-9a-f]{6})(?:[0-9a-f]{2})?$/i.exec(hex)?.[1];
  return digits ? `#${digits.toLowerCase()}` : null;
}
function channels(hex) {
  const normal = normaliseHex(hex);
  if (!normal) throw new TypeError(`Expected a #rrggbb colour, got ${JSON.stringify(hex)}.`);
  const value = Number.parseInt(normal.slice(1), 16);
  return [value >> 16 & 255, value >> 8 & 255, value & 255];
}
function toHex(rgb) {
  return `#${rgb.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}
function linear(channel) {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}
function luminance(hex) {
  const [red, green, blue] = channels(hex);
  return 0.2126 * linear(red) + 0.7152 * linear(green) + 0.0722 * linear(blue);
}
function contrastRatio(a, b) {
  const first = luminance(a);
  const second = luminance(b);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}
function mix(from, to, amount) {
  const [r1, g1, b1] = channels(from);
  const [r2, g2, b2] = channels(to);
  const step = (start, end) => Math.round(start + (end - start) * amount);
  return toHex([step(r1, r2), step(g1, g2), step(b1, b2)]);
}
function adjust(hex, towards, passes, maxSteps = MAX_STEPS) {
  let adjusted = hex;
  for (let step = 0; step < maxSteps && !passes(adjusted); step += 1) {
    adjusted = mix(adjusted, towards, STEP);
  }
  return adjusted;
}
function toOklab(hex) {
  const [red, green, blue] = channels(hex).map(linear);
  const l = Math.cbrt(0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue);
  const m = Math.cbrt(0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue);
  const s = Math.cbrt(0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  ];
}
function fromOklab([lightness, a, b]) {
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const encode = (value) => {
    const clipped = Math.min(1, Math.max(0, value));
    const srgb = clipped <= 31308e-7 ? 12.92 * clipped : 1.055 * clipped ** (1 / 2.4) - 0.055;
    return Math.round(srgb * 255);
  };
  return toHex([
    encode(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    encode(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    encode(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s)
  ]);
}
function calmForDark(hex) {
  const outside = (candidate) => {
    const [lightness2, a2, b2] = toOklab(candidate);
    return lightness2 > DARK_MAX_LIGHTNESS || Math.hypot(a2, b2) > DARK_MAX_CHROMA;
  };
  if (!outside(hex)) return hex;
  const [lightness, a, b] = toOklab(hex);
  const chroma = Math.hypot(a, b);
  let calmed = hex;
  for (let margin = 0; outside(calmed); margin += 2e-3) {
    const scale = Math.min(1, (DARK_MAX_CHROMA - margin) / chroma);
    calmed = fromOklab([Math.min(lightness, DARK_MAX_LIGHTNESS - margin), a * scale, b * scale]);
  }
  return calmed;
}
function hoverColour(primary, onPrimary, passes) {
  const away = onPrimary === WHITE ? BLACK : WHITE;
  const towards = away === BLACK ? WHITE : BLACK;
  for (let steps = HOVER_STEPS; steps > 0; steps -= 1) {
    for (const end of [away, towards]) {
      const hover = mix(primary, end, steps * STEP);
      if (hover !== primary && passes(hover)) return hover;
    }
  }
  return primary;
}
function scrimColour(primary, onPrimary) {
  const worst = onPrimary === WHITE ? WHITE : BLACK;
  const away = onPrimary === WHITE ? BLACK : WHITE;
  return adjust(
    primary,
    away,
    (hex) => contrastRatio(onPrimary, mix(hex, worst, SCRIM_SHOW_THROUGH)) >= 4.5
  );
}
function modeTokens(given, mode) {
  const neutrals = NEUTRALS[mode];
  const towards = mode === "light" ? BLACK : WHITE;
  const brand = mode === "dark" ? calmForDark(given) : given;
  const onBoth = (hex) => Math.min(contrastRatio(hex, neutrals.surface), contrastRatio(hex, neutrals.bg));
  const textOn = (fill) => contrastRatio(WHITE, fill) >= 4.5 ? WHITE : contrastRatio(INK, fill) >= 4.5 ? INK : null;
  let primary = adjust(brand, towards, (hex) => onBoth(hex) >= 3);
  primary = adjust(primary, towards, (hex) => textOn(hex) !== null);
  const onPrimary = textOn(primary) ?? (towards === BLACK ? WHITE : INK);
  const primaryText = mode === "light" ? adjust(brand, towards, (hex) => onBoth(hex) >= 4.5) : adjust(brand, towards, (hex) => onBoth(hex) >= 7, DARK_TEXT_STEPS);
  return {
    ...neutrals,
    primary,
    primaryHover: hoverColour(
      primary,
      onPrimary,
      (hex) => onBoth(hex) >= 3 && contrastRatio(onPrimary, hex) >= 4.5
    ),
    onPrimary,
    primaryText,
    primaryScrim: scrimColour(primary, onPrimary),
    focusRing: primaryText
  };
}
function themeFromBrand(hex) {
  const brand = normaliseHex(hex) ?? DEFAULT_BRAND;
  return { brand, light: modeTokens(brand, "light"), dark: modeTokens(brand, "dark") };
}
function themeStyle(theme) {
  const style = {};
  for (const mode of ["light", "dark"]) {
    for (const [token, name] of Object.entries(BRAND_PROPERTIES)) {
      style[`--tny-${mode}-${name}`] = theme[mode][token];
    }
  }
  return style;
}
function ThemeRoot({
  brand,
  mode = "system",
  className,
  style,
  ...props
}) {
  const classes = ["tny-theme", mode !== "system" && `tny-theme-${mode}`, className].filter(Boolean);
  return createElement2("div", {
    ...props,
    className: classes.join(" "),
    style: brand ? { ...themeStyle(themeFromBrand(brand)), ...style } : style
  });
}
export {
  Alert,
  AppShell,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DEFAULT_BRAND,
  DescriptionList,
  Dialog,
  EmptyState,
  Field,
  FileInput,
  FilterBar,
  FormWizard,
  IconButton,
  Input,
  Menu,
  MultiSelect,
  Muted,
  NavTabs,
  PageHeader,
  Pagination,
  Radio,
  SearchInput,
  Select,
  Skeleton,
  StatTile,
  Stepper,
  Table,
  Tabs,
  Textarea,
  ThemeRoot,
  Timeline,
  ToastProvider,
  ToneIcon,
  WizardFieldError,
  breakable,
  contrastRatio,
  normaliseHex,
  themeFromBrand,
  themeStyle,
  useToast
};
